import { Box, Button, Flex, FormControl, FormLabel, Heading, Input, Spinner, Stack, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import MainInput from "../../../../components/Form/input";
import MainModel from "../../../../components/Models/main-model";
import { brandPalette } from "../../../../constants/brandPalette";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
  withAdminSelectMenuPortal,
} from "../../../../components/Models/admin-modal-theme";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import { getAllGroupsRequest } from "../../../../modules/admin-modules/groups/Actions";
import { createLiveVideoRequest, updateLiveVideoRequest } from "../../../../modules/admin-modules/liveVideos/Actions";
import { closeAddLiveVideo } from "../../../../modules/admin-modules/models/Actions";

function asGroupOption(g) {
  if (g == null) return null;
  if (typeof g === "object") {
    const id = g.id ?? g.student_group_id ?? g.value;
    if (id == null || id === "") return null;
    return { value: id, label: g.name ?? g.label ?? String(id) };
  }
  return { value: g, label: String(g) };
}

/** Live video payload uses `student_groups: [{ id, name }]`. */
function extractLiveVideoGroupOptions(data) {
  if (!data) return [];
  const list = data.student_groups ?? data.studentGroups ?? data.groups;
  if (Array.isArray(list) && list.length) {
    return list.map(asGroupOption).filter(Boolean);
  }
  if (Array.isArray(data.student_group_ids) && data.student_group_ids.length) {
    return data.student_group_ids.map(asGroupOption).filter(Boolean);
  }
  return [];
}

function extractLiveVideoGroupIds(data) {
  return extractLiveVideoGroupOptions(data).map((o) => o.value);
}

/** API datetime → value for input[type=datetime-local] */
function apiDateToDatetimeLocal(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(:\d{2})?/);
  if (m) return `${m[1]}T${m[2]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return "";
}

/** datetime-local → Laravel-friendly "Y-m-d H:i:s" */
function datetimeLocalToApi(val) {
  if (val == null || String(val).trim() === "") return null;
  const raw = String(val).trim();
  const withSeconds = raw.length === 16 ? `${raw}:00` : raw;
  return withSeconds.replace("T", " ");
}

const AddLiveVideo = ({ isAdd, data, setSelectedData }) => {
  const openChapter = useSelector((state) => state.model.isOpen34);
  const isLoading = useSelector((state) => state.adminLiveVideos.isLoading);
  const chapters = useSelector((state) => state.chapters.chapters);
  const chaptersLoading = useSelector((state) => state.chapters.isLoading);
  const groups = useSelector((state) => state.groups.groups);
  const groupsLoading = useSelector((state) => state.groups.isLoading);
  const isOptionsLoading = Boolean(chaptersLoading || groupsLoading);

  const dispatch = useDispatch();
  const [menuPortalTarget, setMenuPortalTarget] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const pendingSave = useRef(false);
  const isFormBusy = isSaving || isOptionsLoading;

  useEffect(() => {
    setMenuPortalTarget(document.body);
  }, []);

  useEffect(() => {
    if (!openChapter) {
      pendingSave.current = false;
      setIsSaving(false);
    }
  }, [openChapter]);

  useEffect(() => {
    if (pendingSave.current && isLoading === false) {
      pendingSave.current = false;
      setIsSaving(false);
    }
  }, [isLoading]);

  const activeChapters = useMemo(
    () =>
      Array.isArray(chapters)
        ? chapters.filter((c) => Number(c?.active) === 1)
        : [],
    [chapters]
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: !isAdd
      ? {
        title: data?.title,
        zoom_link: data?.zoom_link,
        chapter_id: data?.chapter_id ?? data?.chapter?.id,
        start_time: apiDateToDatetimeLocal(data?.start_time),
        student_group_ids: extractLiveVideoGroupIds(data),
      }
      : { student_group_ids: [] },
    shouldUnregister: false,
  });

  const chapterId = watch("chapter_id");
  const studentGroupIds = watch("student_group_ids");
  const assignedGroupOptions = useMemo(
    () => extractLiveVideoGroupOptions(data),
    [data]
  );

  const chapterOptions = useMemo(() => {
    const opts = activeChapters.map((c) => ({ value: c.id, label: c.name }));
    const selectedId = chapterId ?? data?.chapter_id ?? data?.chapter?.id;
    if (
      selectedId != null &&
      selectedId !== "" &&
      !opts.some((o) => String(o.value) === String(selectedId))
    ) {
      const match = (Array.isArray(chapters) ? chapters : []).find(
        (c) => String(c.id) === String(selectedId)
      );
      if (match) {
        opts.unshift({ value: match.id, label: match.name });
      }
    }
    return opts;
  }, [
    activeChapters,
    chapters,
    chapterId,
    data?.chapter_id,
    data?.chapter?.id,
  ]);

  const groupOptions = useMemo(() => {
    const opts = Array.isArray(groups)
      ? groups.map((g) => ({ value: g.id, label: g.name }))
      : [];
    assignedGroupOptions.forEach((g) => {
      if (!opts.some((o) => String(o.value) === String(g.value))) {
        opts.unshift(g);
      }
    });
    return opts;
  }, [groups, assignedGroupOptions]);

  useEffect(() => {
    if (openChapter) {
      dispatch(getAllChaptersRequest());
      dispatch(getAllGroupsRequest());
    }
  }, [openChapter, dispatch]);

  useEffect(() => {
    if (!openChapter) return;
    if (!isAdd && data?.id) {
      reset({
        title: data?.title,
        zoom_link: data?.zoom_link,
        chapter_id: data?.chapter_id ?? data?.chapter?.id,
        start_time: apiDateToDatetimeLocal(data?.start_time),
        student_group_ids: extractLiveVideoGroupIds(data),
      });
    } else if (isAdd) {
      reset({ student_group_ids: [] });
    }
  }, [openChapter, isAdd, data, reset]);

  // After chapters load, map response chapter_id onto the select options.
  useEffect(() => {
    if (!openChapter || isAdd || !Array.isArray(chapters) || chapters.length === 0) {
      return;
    }
    const id = data?.chapter_id ?? data?.chapter?.id;
    if (id == null || id === "") return;
    const match = chapters.find((c) => String(c.id) === String(id));
    if (match) {
      setValue("chapter_id", match.id, { shouldDirty: false });
    }
  }, [
    openChapter,
    isAdd,
    chapters,
    data?.chapter_id,
    data?.chapter?.id,
    setValue,
  ]);

  useEffect(() => {
    if (!openChapter || isAdd) return;
    const ids = extractLiveVideoGroupIds(data);
    setValue("student_group_ids", ids, { shouldDirty: false });
  }, [openChapter, isAdd, data, setValue]);

  const selectedGroupOptions = useMemo(() => {
    const ids = Array.isArray(studentGroupIds)
      ? studentGroupIds
      : assignedGroupOptions.map((o) => o.value);
    return ids.map((id) => {
      const fromOptions = groupOptions.find(
        (o) => String(o.value) === String(id)
      );
      if (fromOptions) return fromOptions;
      const fromAssigned = assignedGroupOptions.find(
        (o) => String(o.value) === String(id)
      );
      if (fromAssigned) return fromAssigned;
      const match = (Array.isArray(groups) ? groups : []).find(
        (g) => String(g.id) === String(id)
      );
      return match
        ? { value: match.id, label: match.name }
        : { value: id, label: String(id) };
    });
  }, [groupOptions, studentGroupIds, assignedGroupOptions, groups]);

  const selectedChapterOption = useMemo(() => {
    if (chapterId == null || chapterId === "") return null;
    const fromOptions = chapterOptions.find(
      (o) => String(o.value) === String(chapterId)
    );
    if (fromOptions) return fromOptions;
    const match = (Array.isArray(chapters) ? chapters : []).find(
      (c) => String(c.id) === String(chapterId)
    );
    return match ? { value: match.id, label: match.name } : null;
  }, [chapterOptions, chapterId, chapters]);

  const selectStyles = useMemo(
    () => withAdminSelectMenuPortal(adminSelectStyles()),
    []
  );

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color={brandPalette.primary}
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        {isAdd ? "Add Live Session" : "Edit Live Session"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Title, meeting link, and student groups
      </Text>
    </Box>
  );
  const onSubmit = async (values) => {
    if (isFormBusy) return;
    const action = () => {
      pendingSave.current = false;
      setIsSaving(false);
      dispatch(closeAddLiveVideo());
      setSelectedData({});
      reset();
    };
    const startTime = datetimeLocalToApi(values?.start_time);
    const groupIds = Array.isArray(values?.student_group_ids)
      ? values.student_group_ids.filter((id) => id != null && id !== "")
      : [];
    let formData = new FormData();
    formData.append("title", values?.title);
    formData.append("zoom_link", values?.zoom_link);
    if (startTime != null) {
      formData.append("start_time", startTime);
    }
    if (values?.chapter_id != null && values?.chapter_id !== "") {
      formData.append("chapter_id", values.chapter_id);
    }
    groupIds.forEach((id, idx) => {
      formData.append(`student_group_ids[${idx}]`, id);
    });
    if (isAdd) {
      if (values?.chapter_id == null || values?.chapter_id === "") {
        toast.error("Please select a lesson (active chapter)");
        return;
      }
      if (!groupIds.length) {
        toast.error("Please select at least one student group");
        return;
      }
      pendingSave.current = true;
      setIsSaving(true);
      dispatch(
        createLiveVideoRequest(
          {
            ...values,
            chapter_id: values.chapter_id,
            start_time: startTime,
            student_group_ids: groupIds,
          },
          action
        )
      );
    } else {
      formData.append("_method", "PUT");
      pendingSave.current = true;
      setIsSaving(true);
      dispatch(updateLiveVideoRequest(data?.id, formData, action));
    }
  };
  const closeFunc = () => {
    if (isSaving) return;
    dispatch(closeAddLiveVideo());
    setSelectedData({});
    reset();
  };
  return (
    <MainModel
      isLoading={isFormBusy}
      header={header}
      closeFunc={closeFunc}
      openVar={openChapter}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="2xl"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
      hideDefaultFooter
    >
      <Box position="relative">
        {isFormBusy && (
          <Flex
            position="absolute"
            inset={0}
            bg="blackAlpha.400"
            zIndex={4}
            align="center"
            justify="center"
            borderRadius="md"
          >
            <VStack
              spacing={3}
              bg="var(--surface-raised)"
              px={6}
              py={5}
              borderRadius="lg"
              boxShadow="lg"
            >
              <Spinner
                size="lg"
                color={brandPalette.primary}
                thickness="3px"
              />
              <Text fontWeight="700" color="var(--text-primary)">
                {isSaving ? "Saving live session…" : "Loading…"}
              </Text>
            </VStack>
          </Flex>
        )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          spacing={6}
          dir="ltr"
          w="100%"
          pointerEvents={isFormBusy ? "none" : "auto"}
          opacity={isFormBusy ? 0.65 : 1}
        >

          <MainInput
            label="Session title"
            defaultValues={data?.title ?? data?.name}
            error={errors?.title?.message}
            placeholder="Live session name"
            register={register}
            name="title"
          />
          <MainInput
            label="Meeting link"
            defaultValues={data?.zoom_link}
            error={errors?.zoom_link?.message}
            placeholder="https://..."
            register={register}
            name="zoom_link"
          />
          <FormControl>
            <FormLabel
              fontWeight="800"
              color="var(--text-primary)"
              fontSize="sm"
              mb={2}
            >
              Start time
            </FormLabel>
            <Input
              type="datetime-local"
              {...register("start_time")}
              defaultValue={apiDateToDatetimeLocal(data?.start_time)}
              bg="var(--surface-muted)"
              color="var(--text-primary)"
              borderRadius="12px"
              border="1px solid"
              borderColor="var(--border-subtle)"
            />
          </FormControl>
          <FormControl>
            <FormLabel
              fontWeight="800"
              color="var(--text-primary)"
              fontSize="sm"
              mb={2}
            >
              Lesson
            </FormLabel>
            <Select
              styles={selectStyles}
              options={chapterOptions}
              value={selectedChapterOption}
              onChange={(opt) =>
                setValue("chapter_id", opt?.value ?? "", {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              placeholder="Select lesson…"
              isClearable
              isDisabled={isFormBusy}
              isLoading={Boolean(chaptersLoading)}
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              maxMenuHeight={240}
              noOptionsMessage={() =>
                chaptersLoading
                  ? "Loading lessons…"
                  : "No active lessons — activate a chapter in Lessons first"
              }
            />
          </FormControl>
          <FormControl>
            <FormLabel
              fontWeight="800"
              color="var(--text-primary)"
              fontSize="sm"
              mb={2}
            >
              Student groups
            </FormLabel>
            <Select
              styles={selectStyles}
              isMulti
              options={groupOptions}
              value={selectedGroupOptions}
              onChange={(opts) =>
                setValue(
                  "student_group_ids",
                  (opts || []).map((o) => o.value),
                  {
                    shouldValidate: true,
                    shouldDirty: true,
                  }
                )
              }
              placeholder="Select student groups…"
              isClearable
              isDisabled={isFormBusy}
              isLoading={Boolean(groupsLoading)}
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              maxMenuHeight={240}
              noOptionsMessage={() =>
                groupsLoading ? "Loading groups…" : "No student groups found"
              }
            />
          </FormControl>
          <Flex justify="flex-end" gap={6} pt={4}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              isDisabled={isSaving}
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isDisabled={isOptionsLoading}
              isLoading={isSaving}
              loadingText="Saving"
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save Changes
            </Button>
          </Flex>
        </Stack>
      </form>
      </Box>
    </MainModel>
  );
};

export default AddLiveVideo;
